import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ListItem, Card, PageHeader } from '@nexply/ui';
import {
  User,
  Users,
  UsersRound,
  Sparkles,
  Map as MapIcon,
  Settings,
  HardDrive,
  HelpCircle,
  Shield,
  Scale,
  FileText,
  Lock,
  Info,
  LogOut,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import { useIsDesktop } from '../../lib/useMediaQuery.js';

interface Row { to?: string; label: string; sub?: string; icon: ReactNode; danger?: boolean; onClick?: () => void; }

export function MorePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const isAdmin = user?.role === 'ADMIN';

  const network: Row[] = [
    { to: '/members', label: 'Mitglieder', sub: 'Liste, Smart Search & Telefon-Kontakte', icon: <Users className="h-5 w-5 text-gold" /> },
    { to: '/map', label: 'Karte', sub: 'Kontakte auf der Karte', icon: <MapIcon className="h-5 w-5 text-gold" /> },
    { to: '/groups', label: 'Netzwerk-Gruppen', sub: 'Gemeinsam ein Netzwerk pflegen', icon: <UsersRound className="h-5 w-5 text-gold" /> },
    { to: '/matches', label: 'Vernetzer', sub: 'KI-Vorschläge prüfen', icon: <Sparkles className="h-5 w-5 text-gold" /> },
  ];
  const account: Row[] = [
    { to: '/profile', label: 'Mein Profil', sub: 'Persönliche Daten & Pitch', icon: <User className="h-5 w-5 text-text-dim" /> },
    { to: '/settings', label: 'Einstellungen', icon: <Settings className="h-5 w-5 text-text-dim" /> },
    { to: '/backup', label: 'Backup', sub: 'Export & Import', icon: <HardDrive className="h-5 w-5 text-text-dim" /> },
    { to: '/help', label: 'Hilfe', icon: <HelpCircle className="h-5 w-5 text-text-dim" /> },
  ];
  const admin: Row[] = isAdmin
    ? [{ to: '/admin', label: 'Admin', sub: 'Analytics & Benutzer', icon: <Shield className="h-5 w-5 text-gold" /> }]
    : [];
  // Impressumspflicht gilt auch im eingeloggten Bereich — die Rechtsseiten sind
  // statische Landing-Seiten und öffnen deshalb in einem neuen Tab.
  const legal: Row[] = [
    { label: 'Impressum', icon: <Info className="h-5 w-5 text-text-dim" />, onClick: () => window.open('/impressum', '_blank') },
    { label: 'Datenschutz', icon: <Lock className="h-5 w-5 text-text-dim" />, onClick: () => window.open('/datenschutz', '_blank') },
    { label: 'AGB', sub: 'Nutzungsbedingungen', icon: <FileText className="h-5 w-5 text-text-dim" />, onClick: () => window.open('/agb', '_blank') },
    { label: 'Rechtliche Hinweise', icon: <Scale className="h-5 w-5 text-text-dim" />, onClick: () => window.open('/rechtliche-hinweise', '_blank') },
  ];

  if (desktop) {
    const all = [
      { caption: 'Netzwerk', rows: network },
      { caption: 'Konto', rows: account },
      ...(admin.length ? [{ caption: 'Admin', rows: admin }] : []),
      { caption: 'Rechtliches', rows: legal },
    ];
    return (
      <div className="space-y-6 animate-slide-up">
        <PageHeader eyebrow="Konto" title="Mehr" subtitle={user?.email} />
        {all.map((sec) => (
          <section key={sec.caption}>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-text-muted">{sec.caption}</h2>
            <div className="grid grid-cols-3 gap-3">
              {sec.rows.map((r) => (
                <Card
                  key={r.label}
                  variant="default"
                  interactive
                  onClick={r.to ? () => nav({ to: r.to as any }) : r.onClick}
                  className="group p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-surface-3">
                      {r.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text">{r.label}</div>
                      {r.sub && <div className="mt-0.5 text-xs text-text-dim">{r.sub}</div>}
                    </div>
                    <ArrowRight className="h-4 w-4 text-text-muted transition group-hover:text-gold" />
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ))}
        <div className="pt-2">
          <button
            onClick={async () => { try { await logout(); } catch {} window.location.assign('/login'); }}
            className="inline-flex items-center gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-2.5 text-sm text-danger hover:bg-danger/15 transition"
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader eyebrow="Konto" title="Mehr" subtitle={user?.email} />

      <Section title="Netzwerk" rows={network} navTo={(to) => nav({ to: to as any })} />
      <Section title="Konto" rows={account} navTo={(to) => nav({ to: to as any })} />
      {admin.length > 0 && <Section title="Admin" rows={admin} navTo={(to) => nav({ to: to as any })} />}
      <Section title="Rechtliches" rows={legal} navTo={(to) => nav({ to: to as any })} />

      <div>
        <ListItem
          leading={<LogOut className="h-5 w-5 text-danger" />}
          title={<span className="text-danger">Abmelden</span>}
          onClick={async () => { try { await logout(); } catch {} window.location.assign('/login'); }}
        />
      </div>
    </div>
  );
}

function Section({ title, rows, navTo }: { title: string; rows: Row[]; navTo: (to: string) => void }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <div className="mb-2.5 px-1 text-xs font-semibold uppercase tracking-wider text-text-muted">{title}</div>
      <div className="space-y-2">
        {rows.map((r) => (
          <ListItem
            key={r.label}
            leading={r.icon}
            title={r.label}
            subtitle={r.sub}
            onClick={r.to ? () => navTo(r.to!) : r.onClick}
            chevron
          />
        ))}
      </div>
    </section>
  );
}
